import useApi from 'common/hooks/useApi';
import useThunkDispatch from 'common/hooks/useThunkDispatch';
import useTypedSelector from 'common/hooks/useTypedSelector';
import { exists } from 'common/services/enum/enumService';
import Address from 'domain/components/application/forms/Address';
import Contact from 'domain/components/application/forms/Contact';
import Details from 'domain/components/application/forms/Details';
import Relationship from 'domain/components/application/forms/Relationship';
import ResidenceUploadContainer from 'domain/components/application/upload/ResidenceUploadContainer';
import getErrorsForStep, {
  isValid,
  validateContact,
} from 'domain/components/application/validate';
import ChsAddress from 'domain/types/ChsAddress';
import RegistrationNavLink from 'features/apply-for-registration/components/RegistrationNavLink';
import { saveDraftApplication } from 'features/apply-for-registration/DraftApplicationSlice';
import arrayMutators from 'final-form-arrays';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Nav,
  TabContainer,
  TabContent,
  TabPane,
} from 'react-bootstrap';
import { Form } from 'react-final-form';
import { ApplicationStep } from '../../../domain/enums/ApplicationStep';
import DraftApplication from '../../../domain/types/DraftApplication';
import ClearStep from './ClearStep';
import DwellingTypes from 'domain/enums/DwellingTypes';

interface IApplication {
  onReviewClick: () => void;
  onClearAllSteps: () => void;
  defaultStep: ApplicationStep;
}

function Application(props: IApplication) {
  const { profileAddress, profileEmail, draftApplication } = useTypedSelector(
    (state) => ({
      profileEmail: state.auth.email,
      profileAddress: state.profile.data.address,
      draftApplication: state.draftApplication,
      token: state.auth.access_token,
    })
  );
  const [step, setStep] = useState(props.defaultStep);
  const [showErrors, setShowErrors] = useState(false);
  const { defaultStep } = props;
  const [{ data: previousAddresses }, getPreviousAddresses] = useApi<
    Array<ChsAddress>
  >({
    url: `/applications/previousAppAddresses`,
    method: 'GET',
  });
  const formContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setStep(defaultStep);
    getPreviousAddresses();
  }, [defaultStep, getPreviousAddresses]);

  // TODO clear application
  // TODO save / validate each step
  // TODO save on navigate away
  // TODO navigate to max step on page load
  const dispatch = useThunkDispatch();

  const navigate = async (next: ApplicationStep, app: DraftApplication) => {
    if (next > step && !isValid(step, app)) {
      return setShowErrors(true);
    }

    setShowErrors(false);
    dispatch(saveDraftApplication(app));
    setStep(next);
    if (formContainerRef.current) {
      formContainerRef.current.scrollIntoView(true);
    }
  };

  const handleReviewClick = async (app: DraftApplication) => {
    if (!isValid(step, app)) {
      return setShowErrors(true);
    }
    setShowErrors(false);
    await dispatch(saveDraftApplication(app));
    props.onReviewClick();
  };

  return (
    <Form<DraftApplication>
      onSubmit={() => {
        return;
      }}
      validate={(values) => {
        if (showErrors) {
          return getErrorsForStep(step, values);
        }
        return undefined;
      }}
      mutators={{ ...arrayMutators }}
      initialValues={draftApplication.data}
    >
      {({
        handleSubmit,
        form: {
          mutators: { push },
        },
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <TabContainer
            activeKey={step}
            onSelect={(next) => {
              if (exists(ApplicationStep, next)) {
                navigate(Number(next), values);
              }
            }}
          >
            <Nav variant='tabs'>
              <RegistrationNavLink
                title='Step 1'
                subTitle='Contact Information'
                step={ApplicationStep.contact}
                disabled={false}
              />
              <RegistrationNavLink
                title='Step 2'
                subTitle='Shared Housing Unit Address'
                step={ApplicationStep.address}
                disabled={!isValid(ApplicationStep.contact, values)}
              />
              <RegistrationNavLink
                title='Step 3'
                subTitle='Relationship to Property'
                step={ApplicationStep.relationship}
                disabled={!isValid(ApplicationStep.address, values)}
              />
              <RegistrationNavLink
                title='Step 4'
                subTitle='More Details'
                step={ApplicationStep.details}
                disabled={!isValid(ApplicationStep.relationship, values)}
              />
            </Nav>
            <TabContent ref={formContainerRef} className='px-4 py-4 border'>
              <TabPane eventKey={ApplicationStep.contact}>
                <Contact
                  profileEmail={profileEmail}
                  data={values}
                  errors={showErrors ? validateContact(values) : {}}
                />
              </TabPane>
              <TabPane eventKey={ApplicationStep.address}>
                <Address
                  previousAddresses={previousAddresses}
                  profileAddress={profileAddress}
                  data={values}
                />
              </TabPane>
              <TabPane eventKey={ApplicationStep.relationship}>
                <Relationship
                  data={values}
                  showErrors={showErrors}
                  proof={
                    <ResidenceUploadContainer showValidations={showErrors} />
                  }
                />
              </TabPane>
              <TabPane eventKey={ApplicationStep.details}>
                <Details
                  dwellingTypeCd={values.dwellingTypeCd as DwellingTypes}
                />
              </TabPane>
            </TabContent>
          </TabContainer>
          <div className='d-flex mt-2'>
            <div className='mr-auto'>
              <ClearStep profileEmail={profileEmail} step={step} />
              <Button
                type='button'
                variant='link'
                onClick={() => {
                  props.onClearAllSteps();
                }}
              >
                Clear all steps
              </Button>
            </div>
            <div>
              {step + 1 < ApplicationStep.review ? (
                <Button
                  type='button'
                  onClick={() => navigate(step + 1, values)}
                >{`Save & Continue`}</Button>
              ) : (
                <Button
                  type='button'
                  onClick={() => handleReviewClick(values)}
                >{`Review Application`}</Button>
              )}
            </div>
          </div>
        </form>
      )}
    </Form>
  );
}

export default Application;
